import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ReactSVG } from 'react-svg';
import useColumnConfig, { getStoredPageSize } from '../hooks/useColumnConfig';
import ColumnConfigDrawer from '../components/ColumnConfigDrawer';
import TableFilterDrawer, { TableFilterButton, countActiveFilters } from '../components/TableFilterDrawer';
import { Alert, Button, Card, Segmented, Spin, Table, Tag, Tooltip, Typography, message } from 'antd';
import { CheckOutlined, ReloadOutlined, SafetyCertificateOutlined, SearchOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { getIntegrityViolations, resolveIntegrityViolation, runIntegritySweep } from '../modules/integrity/api';

const { Paragraph } = Typography;

const fmtTime = (iso) => (iso ? dayjs(iso).format('DD MMM YYYY HH:mm') : '—');

// Codes the backend writes (common/integrity/route-integrity.ts + the order
// state machine). Unknown codes still render, just without an explanation.
const CODES = [
  'ILLEGAL_TRANSITION',
  'ORPHAN_STEP',
  'MISSING_LEG',
  'DUPLICATE_LEG',
  'COMPLETED_STEP_OPEN_ORDER',
  'OPEN_STEP_FINISHED_ORDER',
  'ROUTED_ORDER_NOT_PLANNED',
  'TRASHED_ORDER_STEP',
  'TRASHED_ORDER_ON_ROUTE',
];

/**
 * #149 — Monitoring → Integrity. Every place that rewrites route steps checks
 * the route afterwards, and every order status change is checked against the
 * allowed transitions; in prod a violation is recorded here instead of
 * breaking the request. A nightly sweep re-checks the last 14 days and
 * resolves what has been fixed.
 */
const IntegrityPage = () => {
  const { t } = useTranslation(['routes', 'common']);
  const [loading, setLoading] = useState(false);
  const [sweeping, setSweeping] = useState(false);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(() => getStoredPageSize('integrity-columns', 50));
  const [status, setStatus] = useState('open');
  const [filterOpen, setFilterOpen] = useState(false);
  const [filters, setFilters] = useState({});
  const [resolvingId, setResolvingId] = useState(null);
  const [messageApi, contextHolder] = message.useMessage();

  const load = useCallback(
    async (p = page, ps = pageSize, s = status, f = filters) => {
      setLoading(true);
      try {
        const { response } = await getIntegrityViolations({ take: ps, skip: (p - 1) * ps, status: s, ...f });
        setRows(response?.items || []);
        setTotal(response?.total || 0);
      } catch (e) {
        messageApi.error(e?.message || t('common:error'));
      } finally {
        setLoading(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [page, pageSize, status, filters],
  );

  useEffect(() => {
    load(1, pageSize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSweep = async () => {
    setSweeping(true);
    try {
      const to = dayjs().format('YYYY-MM-DD');
      const from = dayjs().subtract(14, 'day').format('YYYY-MM-DD');
      const { response } = await runIntegritySweep({ dateFrom: from, dateTo: to, persist: true });
      messageApi.success(
        t('integrity.sweepDone', {
          routes: response?.checkedRoutes ?? 0,
          found: response?.violations?.length ?? 0,
          resolved: response?.resolved ?? 0,
        }),
      );
      setPage(1);
      load(1, pageSize);
    } catch (e) {
      messageApi.error(e?.message || t('common:error'));
    } finally {
      setSweeping(false);
    }
  };

  const handleResolve = async (rec) => {
    setResolvingId(rec.id);
    try {
      await resolveIntegrityViolation(rec.id);
      messageApi.success(t('integrity.resolved'));
      load(page, pageSize);
    } catch (e) {
      messageApi.error(e?.message || t('common:error'));
    } finally {
      setResolvingId(null);
    }
  };

  const columns = [
    {
      title: t('integrity.lastSeen'),
      dataIndex: 'lastSeenAt',
      key: 'lastSeenAt',
      width: 150,
      render: (v) => <span style={{ whiteSpace: 'nowrap' }}>{fmtTime(v)}</span>,
    },
    {
      title: t('integrity.code'),
      dataIndex: 'code',
      key: 'code',
      width: 230,
      render: (code, rec) => (
        <Tooltip title={CODES.includes(code) ? t(`integrity.codes.${code}`) : null}>
          <Tag color={rec.kind === 'ORDER_TRANSITION' ? 'orange' : 'red'}>{code}</Tag>
        </Tooltip>
      ),
    },
    {
      title: t('integrity.route'),
      dataIndex: 'routeId',
      key: 'routeId',
      width: 90,
      render: (id) => (id ? <Link to={`/admin/routes/${id}`}>#{id}</Link> : '—'),
    },
    {
      title: t('integrity.order'),
      dataIndex: 'orderCode',
      key: 'orderCode',
      width: 130,
      render: (code) => (code ? <Link to={`/admin/orders/${code}`}>{code}</Link> : '—'),
    },
    {
      title: t('integrity.detail'),
      dataIndex: 'detail',
      key: 'detail',
    },
    {
      title: t('integrity.occurrences'),
      dataIndex: 'occurrences',
      key: 'occurrences',
      width: 90,
      align: 'right',
    },
    {
      title: '',
      key: 'actions',
      width: 150,
      render: (_, rec) =>
        rec.resolvedAt ? (
          <Tag color="green">{t('integrity.reviewed')}</Tag>
        ) : (
          <Button
            type="link"
            size="small"
            icon={<CheckOutlined />}
            loading={resolvingId === rec.id}
            onClick={() => handleResolve(rec)}
          >
            {t('integrity.markReviewed')}
          </Button>
        ),
    },
  ];

  const expanded = (rec) => (
    <div style={{ fontSize: 13 }}>
      <div>
        <b>{t('integrity.firstSeen')}</b> {fmtTime(rec.firstSeenAt)} · <b>{t('integrity.context')}</b>{' '}
        {rec.context || '—'}
      </div>
      {CODES.includes(rec.code) && <div style={{ marginTop: 4 }}>{t(`integrity.codes.${rec.code}`)}</div>}
      <Paragraph code copyable style={{ marginTop: 8, fontSize: 12 }}>
        {rec.fingerprint}
      </Paragraph>
    </div>
  );

  const FIXED_KEYS = ['actions'];
  const columnConfig = useColumnConfig('integrity-columns', columns, FIXED_KEYS);

  return (
    <div className="IntegrityPage Content" style={{ paddingBottom: 24 }}>
      {contextHolder}
      <Card>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            gap: 16,
            marginBottom: 16,
            flexWrap: 'wrap',
          }}
        >
          <div>
            <h2 style={{ margin: 0 }}>
              <SafetyCertificateOutlined style={{ color: '#cf1322', marginRight: 8 }} />
              {t('integrity.title')}
            </h2>
            <div style={{ color: 'var(--text-muted)', maxWidth: 760, marginTop: 4 }}>{t('integrity.subtitle')}</div>
          </div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
            <Segmented
              value={status}
              onChange={(s) => {
                setStatus(s);
                setPage(1);
                load(1, pageSize, s);
              }}
              options={[
                { value: 'open', label: t('integrity.open') },
                { value: 'resolved', label: t('integrity.reviewedTab') },
                { value: 'all', label: t('integrity.all') },
              ]}
            />
            <Button icon={<SearchOutlined />} loading={sweeping} onClick={handleSweep}>
              {t('integrity.checkNow')}
            </Button>
            <Button type="primary" icon={<ReloadOutlined />} loading={loading} onClick={() => load(page, pageSize)}>
              {t('integrity.refresh')}
            </Button>
            <TableFilterButton activeCount={countActiveFilters(filters)} onClick={() => setFilterOpen(true)} />
            <Button
              type="text"
              title="Settings"
              onClick={columnConfig.openDrawer}
              style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}
            >
              <ReactSVG className="icon" src="/icons/settings.svg" />
            </Button>
          </div>
        </div>

        <Spin spinning={loading}>
          {rows.length === 0 && !loading ? (
            <Alert type="success" showIcon message={t('integrity.allClear')} />
          ) : (
            <Table
              rowKey="id"
              size="small"
              columns={columnConfig.visibleColumns}
              dataSource={rows}
              expandable={{ expandedRowRender: expanded }}
              pagination={{
                current: page,
                pageSize,
                total,
                showSizeChanger: false,
                onChange: (p, ps) => {
                  setPage(p);
                  setPageSize(ps);
                  load(p, ps);
                },
              }}
            />
          )}
        </Spin>
        <TableFilterDrawer
          open={filterOpen}
          onClose={() => setFilterOpen(false)}
          values={filters}
          onApply={(f) => {
            setFilters(f);
            setPage(1);
            load(1, pageSize, status, f);
          }}
          fields={[
            {
              name: 'kind',
              label: t('integrity.kind'),
              placeholder: '—',
              options: [
                { value: 'ROUTE', label: t('integrity.kindRoute') },
                { value: 'ORDER_TRANSITION', label: t('integrity.kindTransition') },
              ],
            },
          ]}
        />
        <ColumnConfigDrawer
          open={columnConfig.drawerOpen}
          onClose={columnConfig.closeDrawer}
          columns={columnConfig.orderedAllColumns}
          hidden={columnConfig.hidden}
          fixedKeys={FIXED_KEYS}
          onToggle={columnConfig.toggleColumn}
          onMove={columnConfig.moveColumn}
          onReset={columnConfig.resetConfig}
          pageSize={columnConfig.pageSize}
          onPageSizeChange={(size) => {
            columnConfig.setPageSize(size);
            setPageSize(size);
            setPage(1);
            load(1, size);
          }}
        />
      </Card>
    </div>
  );
};

export default IntegrityPage;
